import React from "react";
import { SubscriptionPlan } from "enums/subscription-plans";
import { usePricingData } from "features/Pricing";
import { SubscriptionPlanIcon } from "features/Pricing/utils";
import useLabels from "hooks/useLabels";
import LoadingSubscriptionCard from "../LoadingSubscriptionCard/LoadingSubscriptionCard";
import PricingFeatures from "features/Pricing/components/PricingFeatures";

type SubscriptionCurrentPlanCardProps = {
  currentPlan: SubscriptionPlan;
  isLoading: boolean;
};

/**
 * @component SubscriptionCurrentPlanCard
 * @description Displays the features of the user's current plan.
 * @example
 * return (
 *   <SubscriptionCurrentPlanCard
 *     currentPlan={currentPlan}
 *     isLoading={isLoading}
 *     subscription={subscription}
 *   />
 * )
 */
const SubscriptionCurrentPlanCard = ({
  currentPlan,
  isLoading,
}: SubscriptionCurrentPlanCardProps) => {
  const [currentPlanCardLabels] = useLabels("current-plan-card");
  const { pricingData } = usePricingData();
  const { premium, free, deep, enterprise, team } = pricingData || {};

  let featuresList: string[] = [];
  let title = "";

  if (currentPlan === SubscriptionPlan.FREE || currentPlan === undefined) {
    featuresList = free?.features ?? [];
    title = currentPlanCardLabels["title-free"];
  } else if (currentPlan === SubscriptionPlan.PREMIUM) {
    featuresList = premium?.features ?? [];
    title = currentPlanCardLabels["title-premium"];
  } else if (currentPlan === SubscriptionPlan.DEEP) {
    featuresList = deep?.features ?? [];
    title = currentPlanCardLabels["title-deep"];
  } else if (currentPlan === SubscriptionPlan.TEAM) {
    featuresList = team?.features ?? [];
    title = currentPlanCardLabels["title-team"];
  } else if (currentPlan === SubscriptionPlan.ENTERPRISE) {
    featuresList = enterprise?.features ?? [];
    title = currentPlanCardLabels["title-enterprise"];
  }

  if (isLoading) {
    return <LoadingSubscriptionCard />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 items-center">
        <SubscriptionPlanIcon plan={currentPlan || SubscriptionPlan.FREE} />
        <h3 className="flex-1 lg-bold">{title}</h3>
      </div>
      <PricingFeatures featuresList={featuresList} />
    </div>
  );
};

export default SubscriptionCurrentPlanCard;
